/**
 * The single source of truth for PocketDungeon.
 * Plain serializable data only — no class instances, functions, or DOM.
 * Every other module reads/writes through core functions, never mutates blindly.
 */

export const SAVE_VERSION = 16;

/** A fresh, empty 8-slot equipment set (each hero owns its own). */
export function emptyEquipped() {
  return {
    weapon: null,
    helmet: null,
    chest: null,
    boots: null,
    gloves: null,
    ring1: null,
    ring2: null,
    amulet: null,
  };
}

/**
 * Create a fresh game state for a brand-new player.
 * @param {number} [seed] optional RNG seed for reproducibility
 * @returns {object} a serializable game state
 */
export function createInitialState(seed) {
  return {
    saveVersion: SAVE_VERSION,
    createdAt: Date.now(),
    lastSavedAt: Date.now(),
    rngSeed: (seed ?? (Math.random() * 2 ** 32)) >>> 0,

    // Meta / progression
    player: {
      name: 'Herói',
      gold: 0,
    },

    // Whether the player has picked a class yet (false => show hero select).
    heroChosen: false,

    // The active party (1 or 2 heroes). Each hero owns its own equipment and
    // death lockout, so a downed hero can be parked while the other plays on.
    party: [
      {
        classId: 'knight',
        level: 1,
        xp: 0,
        hp: 100,
        maxHp: 100,
        alive: true,
        reviveAt: 0, // timestamp when a downed hero revives
        lockedUntil: 0, // death lockout (ms); 0 = playable
        skills: [],       // learned skill ids
        skillPoints: 0,   // unspent points
        activeSkillVisual: null,
        equipped: emptyEquipped(),
      },
    ],

    // Per-class progression for classes that aren't currently active.
    // Keyed by classId; the active heroes live in party. Each class levels up
    // independently (with its own equipped set) and starts at level 1 the first
    // time it's selected.
    heroStash: {},

    // Where the party is fighting.
    activeMapId: 'green_meadows',
    currentMonsters: [], // populated by the loop when combat starts (1-3, boss = 1)
    killsSinceBoss: 0,   // regular kills on the map; at boss.everyKills the boss spawns

    // Loot the player owns. Each equipment instance has a uuid for trade coherence.
    inventory: [],

    // Stacking consumables.
    consumables: {
      foods: {}, // { foodId: qty }
      reviveKits: 0,
      protectionAmulets: 0, // consumed on dungeon entry: -30% incoming dmg for that run
    },

    // Daily dungeon: one attempt per account per day (not per hero). A tougher,
    // solo-boss encounter with guaranteed rare+ loot; entering commits the
    // day's attempt whether you win or lose.
    dungeon: { availableAt: 0 }, // 0 = ready now
    dungeonRun: null, // { dungeonId, protected } while a dungeon fight is active

    // Lifetime stats (for UI/achievements later).
    stats: {
      kills: 0,
      totalGold: 0,
      ticks: 0,
      deaths: 0,
    },
  };
}
